<template>
    <div>
        <!-- =============== Page Title ================-->
        <div class="level is-vcentered">
            <div class="level-left">
                <h1 class="title is-size-4">Project List</h1>
            </div>
            <div class="level-right">
                <net-base-auth v-if="$can('create','Project')"/>
                <router-link v-if="$can('manage','Admin')" class="level-item button is-info is-outlined" to="/Admin">Admin</router-link>
            </div>
        </div>
        <!-- =============== Search Field ================-->
        <b-field grouped>
            <b-input rounded v-model="searchName" placeholder="search..." icon="search" expanded></b-input>
            <b-checkbox v-model="showArchivedProjects">Show Archived Projects</b-checkbox>
        </b-field>

        <!-- =============== Project List ================-->
        <b-table
            class="box"
            :data="filteredProjects"
            :pagination-simple="false"
            :default-sort-direction="'desc'"
            :striped="true"
            :loading="isLoading"
            :hoverable="true"
            default-sort="lastModified"
            @click="loadProjectDashboard"
            :row-class="() => 'project-item'"
        >
            <template slot-scope="props">
                <b-table-column field="isSharedWithEveryone" width="20">
                    <b-icon pack="fas" size="is-small" title="Shared with everyone" icon="link" v-if="props.row.isSharedWithEveryone"></b-icon>
                </b-table-column>
                <b-table-column field="name" label="Name" sortable>
                    {{ props.row.name }}
                </b-table-column>
                <b-table-column field="state" label="State" centered sortable>
                    <span class="tag" :class="projectServiceHelper.getStateStyle(props.row.state)">{{ props.row.state }}</span>
                </b-table-column>
                <b-table-column label="Topics status" centered sortable>
                    <b-icon 
                        v-if="props.row.topicsState === constants.TOPIC_AND_PAGE_STATE.AVAILABLE" 
                        type="is-success" title="All project topics are available" pack="fas" size="is-small" icon="check">
                    </b-icon>
                    <b-icon 
                        v-if="props.row.topicsState === constants.TOPIC_AND_PAGE_STATE.UNAVAILABLE" 
                        type="is-danger" title="Project contains unavailable topics" pack="fas" size="is-small" icon="times">
                    </b-icon>
                    <b-icon 
                        v-if="props.row.topicsState === constants.TOPIC_AND_PAGE_STATE.NOT_CHECKED" 
                        title="The availability of topics has not yet been checked" pack="fas" class="not-checked-status-icon" custom-class="fa-question-circle" size="is-small">
                    </b-icon>
                </b-table-column>
                <b-table-column label="Pages status" centered sortable>
                    <b-icon 
                        v-if="props.row.pagesState === constants.TOPIC_AND_PAGE_STATE.AVAILABLE" 
                        type="is-success" title="All project pages are active" pack="fas" size="is-small" icon="check">
                    </b-icon>
                    <b-icon 
                        v-if="props.row.pagesState === constants.TOPIC_AND_PAGE_STATE.UNAVAILABLE" 
                        type="is-danger" title="Project contains inactive pages" pack="fas" size="is-small" icon="times">
                    </b-icon>
                    <b-icon 
                        v-if="props.row.pagesState === constants.TOPIC_AND_PAGE_STATE.NOT_CHECKED" 
                        title="The availability of pages has not yet been checked" pack="fas" class="not-checked-status-icon" custom-class="fa-question-circle" size="is-small">
                    </b-icon>
                </b-table-column>
                <b-table-column field="startDate" label="Start Date" centered sortable>
                    {{ props.row.startDate | formatDate }}
                </b-table-column>
                <b-table-column field="endDate" label="End Date" centered sortable>
                    {{ props.row.endDate | formatDate }}
                </b-table-column>
                <b-table-column field="lastModified" label="Last Modified" centered sortable>
                    {{ props.row.lastModified | formatDate }}
                </b-table-column>
                <b-table-column label="" width="100" centered>
                    <div @click.stop>
                        <button v-if="$can('update', props.row) && isProjectOwner(props.row) && (props.row.state !== constants.PROJECT_STATE.PUBLISHING)" class="button is-outlined is-inline-block" @click="$router.push(`/project/edit/${props.row.id}`)">
                            <b-icon icon="edit" :type="props.row.isActive ? 'is-primary' : 'has-text-grey'"></b-icon>
                        </button>
                        <button v-else class="button is-outlined is-inline-block" @click="$router.push(`/project/view/${props.row.id}`)">
                            <b-icon icon="eye" :type="props.row.isActive ? 'is-primary' : 'has-text-grey'"></b-icon>
                        </button>
                    </div>
                </b-table-column>
            </template>
            <!-- Empty Table Template -->
            <template slot="empty">
                <empty-table-content/>
            </template>
        </b-table>
    </div>
</template>

<script>
import ProjectService from '@/services/project-service.js';
import { projectServiceHelper } from '@/services/project-service-helper.js';
import NetBaseAuth from '@/components/NetBaseAuth.vue';
import constants from '@/constants.js';
import EmptyTableContent from '@/components/EmptyTableContent.vue';

export default {
    name: 'ProjectList',
    components: {
        NetBaseAuth,
        EmptyTableContent
    },
    data () {
        let userId = localStorage.getItem('userId');
        return {
            projectServiceHelper,
            projects: [],
            errorMessage: '',
            isLoading: true,
            searchName: '',
            userId: userId,
            constants: constants,
            showArchivedProjects: true
        };
    },
    created () {
        ProjectService.getProjectsForUser(this.userId)
            .then(
                response => {
                    this.projects = response.data;
                    this.projects.forEach(p => { p.__caslType = 'Project'; });

                    this.projects.map(project => {
                        project.topicsState = this.getTopicsState(project);
                        project.pagesState = this.getPagesState(project);
                        return project;
                    });
                }
            ).catch(
                error => {
                    this.errorMessage = error.message;
                }
            ).finally(
                () => {
                    this.isLoading = false;
                }
            );
    },
    methods: {
        loadProjectDashboard (project) {
            if (project.state !== constants.PROJECT_STATE.DRAFT &&
                project.state !== constants.PROJECT_STATE.PUBLISHING) {
                this.$router.push({ path: `/project/dashboard/${project.id}` });
            } else {
                this.$buefy.toast.open({
                    message: project.state === constants.PROJECT_STATE.DRAFT
                        ? ' Cannot view the dashboard for a draft project'
                        : ' Please wait until the project is published...',
                    type: 'is-warning'
                });
            }
        },
        isProjectOwner (project) {
            let userId = localStorage.getItem('userId');
            let projectUserId = project.userId ? project.userId.toString() : '';
            return userId === projectUserId;
        },
        getTopicsState (project) {
            let settings = JSON.parse(project.projectJSON);
            let topicsAvailability = settings.columns.map(col => col.brandTopic.isAvailable);
            topicsAvailability = [...topicsAvailability, settings.conversationTopic.isAvailable];

            if (topicsAvailability.every(v => v === undefined)) {
                return constants.TOPIC_AND_PAGE_STATE.NOT_CHECKED;
            };
            if (topicsAvailability.some(v => v === false)) {
                return constants.TOPIC_AND_PAGE_STATE.UNAVAILABLE;
            };

            return constants.TOPIC_AND_PAGE_STATE.AVAILABLE;
        },
        getPagesState (project) {
            let settings = JSON.parse(project.projectJSON);
            let pagesInfo = settings.columns.map(col => {
                return [col.fbPageInfo, col.twPageInfo, col.ytPageInfo, col.igPageInfo];
            }).flat();

            if (pagesInfo.every(i => i === undefined || i.id === '')) {
                return constants.TOPIC_AND_PAGE_STATE.NOT_CHECKED;
            }
            if (pagesInfo.some(i => i !== undefined && i.id !== '' && i.isActive === false)) {
                return constants.TOPIC_AND_PAGE_STATE.UNAVAILABLE;
            }

            return constants.TOPIC_AND_PAGE_STATE.AVAILABLE;
        }
    },
    computed: {
        filteredProjects () {
            return this.projects.filter((project) => {
                return (this.showArchivedProjects ? true : project.state !== constants.PROJECT_STATE.ARCHIVED) &&
                        project.name
                            .toString()
                            .toLowerCase()
                            .includes(this.searchName.toLowerCase());
            });
        }
    }
};
</script>

<style lang="scss" scoped>
    .project-item {
        td {
            cursor: pointer;
        }
    }
    .not-checked-status-icon {
        color: lightgray;
    }
</style>
